import { useEffect, useRef, useState, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import confetti from "canvas-confetti";
import { http } from "@/api/client";
import { toast } from "sonner";

const LABEL_BY_KEY = { quiz: "Niveau 1", difficile: "Niveau 2", legend: "Légende" };
const labelOf = (k) => LABEL_BY_KEY[k] || k;
const COLORS = ["#e21b3c", "#00a86b", "#d89e00", "#26890c"];
const TIMES = { quiz: 30, difficile: 25, legend: 15 };

export default function Quiz() {
  const { cid, level } = useParams();
  // Remount a fresh session whenever the course or level changes —
  // fixes the "Lancer le niveau suivant" button keeping the stale "done" state.
  return <QuizSession key={`${cid}-${level}`} cid={cid} level={level} />;
}

function QuizSession({ cid, level }) {
  const nav = useNavigate();
  const [quiz, setQuiz] = useState(null);
  const [idx, setIdx] = useState(0);
  const [phase, setPhase] = useState("read");
  const [timeLeft, setTimeLeft] = useState(TIMES[level] || 30);
  const [points, setPoints] = useState(0);
  const [correct, setCorrect] = useState(0);
  const [responses, setResponses] = useState([]);
  const [answers, setAnswers] = useState([]);
  const [result, setResult] = useState(null);
  // For fill: map of blank_index -> word currently placed
  const [fillSlots, setFillSlots] = useState({});
  // For fill: per-blank instant feedback status (idle / checking / correct / wrong)
  const [blankStatus, setBlankStatus] = useState({});
  // For fill: count of wrong attempts per blank (to show "Reveal answer" after 2)
  const [wrongCount, setWrongCount] = useState({});
  // For MCQ: instant feedback for the current question { user, correct, ok, explanation }
  const [mcqFeedback, setMcqFeedback] = useState(null);
  const tRef = useRef(null);
  const startRef = useRef(null);
  const quizStartRef = useRef(Date.now());

  useEffect(() => {
    http.get(`/courses/${cid}/quiz?level=${level}`).then((r) => {
      if (!r.data.questions?.length) {
        toast.error("Pas encore de questions pour ce cours");
        nav("/app/courses");
        return;
      }
      setQuiz(r.data);
    }).catch((e) => {
      toast.error(e?.response?.data?.detail || "Accès refusé");
      nav("/app/courses");
    });
  }, [cid, level, nav]);

  // Reset fill slots whenever question changes
  useEffect(() => { setFillSlots({}); setBlankStatus({}); setWrongCount({}); setMcqFeedback(null); }, [idx]);

  // Per-blank live check: query the backend for instant feedback on a single blank.
  const checkBlank = useCallback((blankIndex, word) => {
    const seed = quiz?.seed;
    if (!seed || !word) return;
    setBlankStatus(s => ({ ...s, [blankIndex]: "checking" }));
    http.post("/quiz/check-blank", {
      course_id: cid, level, seed,
      q_index: idx, blank_index: blankIndex, word,
    }).then(r => {
      const ok = r.data.ok;
      setBlankStatus(s => ({ ...s, [blankIndex]: ok ? "correct" : "wrong" }));
      if (!ok) setWrongCount(c => ({ ...c, [blankIndex]: (c[blankIndex] || 0) + 1 }));
    }).catch(() => {
      setBlankStatus(s => ({ ...s, [blankIndex]: "idle" }));
    });
  }, [cid, level, idx, quiz?.seed]);

  // Reveal the expected word for a blank (after 2+ wrong attempts).
  const revealBlank = useCallback(async (blankIndex) => {
    const seed = quiz?.seed;
    if (!seed) return;
    try {
      const r = await http.post("/quiz/reveal-blank", {
        course_id: cid, level, seed, q_index: idx, blank_index: blankIndex,
      });
      const word = r.data.word;
      if (word) {
        setFillSlots(s => ({ ...s, [blankIndex]: word }));
        setBlankStatus(s => ({ ...s, [blankIndex]: "revealed" }));
      }
    } catch (err) {
      toast.error("Impossible d'afficher la réponse");
    }
  }, [cid, level, idx]);

  // Place / clear a word in a blank — triggers an instant per-blank check.
  const setBlank = useCallback((blankIndex, word) => {
    setFillSlots(s => ({ ...s, [blankIndex]: word }));
    if (word) checkBlank(blankIndex, word);
    else setBlankStatus(s => ({ ...s, [blankIndex]: "idle" }));
  }, [checkBlank]);

  const finish = useCallback(async (finalAnswers, finalResponses) => {
    const duration = Date.now() - quizStartRef.current;
    try {
      const r = await http.post("/quiz/submit", {
        course_id: cid, level,
        answers: finalAnswers,
        response_times_ms: finalResponses,
        duration_ms: duration,
        seed: quiz?.seed,
      });
      setResult(r.data);
      setCorrect(r.data.correct);
      setPoints(r.data.points);
    } catch (err) {
      console.error("Quiz submit failed:", err);
      toast.error(err?.response?.data?.detail || "Erreur lors de l'enregistrement du quiz");
    }
    setPhase("done");
  }, [cid, level, quiz?.seed]);

  const onAnswer = useCallback((answer) => {
    clearInterval(tRef.current);
    const elapsed = Date.now() - (startRef.current || Date.now());
    setAnswers((a) => [...a, answer]);
    setResponses((r) => [...r, elapsed]);
    setPhase("between");
    // Trigger instant MCQ feedback (fire-and-forget). Fill questions handle their own feedback.
    const cur = quiz?.questions?.[idx];
    const seed = quiz?.seed;
    if (cur && cur.type !== "fill" && seed && typeof answer === "number") {
      http.post("/quiz/check-mcq", {
        course_id: cid, level, seed, q_index: idx, answer,
      }).then(r => {
        setMcqFeedback({ user: answer, correct: r.data.correct_index, ok: r.data.ok, explanation: r.data.explanation });
      }).catch(() => { /* silent — final score still computed at submit */ });
    }
  }, [cid, level, idx, quiz]);

  const submitFill = useCallback(() => {
    const cur = quiz?.questions?.[idx];
    if (!cur) return;
    const nb = cur.num_blanks || 0;
    const arr = [];
    for (let i = 0; i < nb; i++) arr.push(fillSlots[i] || "");
    onAnswer(arr);
  }, [idx, fillSlots, onAnswer, quiz]);

  useEffect(() => {
    if (!quiz || phase === "done") return;
    if (phase === "read") {
      setTimeLeft(quiz.time_per_question);
      const t = setTimeout(() => { startRef.current = Date.now(); setPhase("answer"); }, 5000);
      return () => clearTimeout(t);
    }
    if (phase === "answer") {
      // No countdown for fill-in-the-blank questions: the student has unlimited time
      if (quiz.questions[idx]?.type === "fill") return;
      tRef.current = setInterval(() => {
        setTimeLeft((s) => {
          if (s <= 0.1) { clearInterval(tRef.current); onAnswer(-1); return 0; }
          return s - 0.1;
        });
      }, 100);
      return () => clearInterval(tRef.current);
    }
    if (phase === "between") {
      const t = setTimeout(() => {
        if (idx + 1 >= quiz.questions.length) finish(answers, responses);
        else { setIdx(idx + 1); setPhase("read"); }
      }, 3500);
      return () => clearTimeout(t);
    }
  }, [phase, quiz, idx, onAnswer, finish, answers, responses]);

  // Auto-submit fill question once all blanks are filled (after a short visual delay).
  useEffect(() => {
    if (!quiz || phase !== "answer") return;
    const cur = quiz.questions[idx];
    if (cur?.type !== "fill") return;
    const nb = cur.num_blanks || 0;
    if (nb === 0) return;
    const filled = Array.from({ length: nb }, (_, k) => k).every(k => fillSlots[k]);
    if (!filled) return;
    const t = setTimeout(() => submitFill(), 500);
    return () => clearTimeout(t);
  }, [fillSlots, quiz, idx, phase, submitFill]);

  // Confetti celebration when a new level gets unlocked 🎉
  useEffect(() => {
    if (phase !== "done" || !result?.next_level_unlocked) return;
    const fire = (opts) => confetti({ particleCount: 90, spread: 80, zIndex: 9999, ...opts });
    fire({ origin: { x: 0.5, y: 0.55 } });
    const t1 = setTimeout(() => fire({ origin: { x: 0.2, y: 0.6 }, angle: 60 }), 350);
    const t2 = setTimeout(() => fire({ origin: { x: 0.8, y: 0.6 }, angle: 120 }), 700);
    return () => { clearTimeout(t1); clearTimeout(t2); };
  }, [phase, result]);

  if (!quiz) return <div className="text-[#5b6577]" data-testid="quiz-loading">Chargement…</div>;
  const q = quiz.questions[idx];
  const total = quiz.time_per_question;
  const progress = (timeLeft / total) * 100;

  if (phase === "done") {
    const pass = result?.passed;
    const nextUnlocked = result?.next_level_unlocked;
    const nextLevel = result?.next_level;
    const review = result?.review || [];
    return (
      <div className="max-w-3xl mx-auto" data-testid="quiz-done">
        <div className="text-center bg-white rounded-3xl p-10 border border-black/5 shadow-sm">
          <div className="font-headings font-black text-4xl text-[#1a2332]">{pass ? "Bravo !" : "Presque !"}</div>
          <div className="mt-4 text-[#5b6577]">Score : <span className="text-[#00a86b] font-bold">{correct}/{quiz.questions.length}</span> · Points : <span className="text-[#FF8C42] font-bold">{points}</span></div>
          <div className="mt-2 text-sm text-[#5b6577]" data-testid="quiz-done-msg">
            {pass ? (
              nextUnlocked ? <>Niveau <span className="text-[#FF8C42] font-bold">{labelOf(nextLevel)}</span> débloqué !</>
              : "Niveau maximal atteint pour ce cours."
            ) : <>Max 1 faute autorisée — rejouez pour débloquer le niveau suivant.</>}
          </div>
          <div className="mt-8 flex gap-3 justify-center flex-wrap">
            <button onClick={() => nav("/app/courses")} data-testid="quiz-back"
              className="bg-[#FF8C42] hover:bg-[#e8763a] text-white font-headings font-bold rounded-xl px-6 py-3">
              Retour aux cours
            </button>
            {nextUnlocked && (
              <button onClick={() => nav(`/app/courses/${cid}/quiz/${nextLevel}`)} data-testid="quiz-next-level"
                className="bg-[#00a86b] hover:bg-[#008855] text-white font-headings font-bold rounded-xl px-6 py-3">
                Lancer {labelOf(nextLevel)} →
              </button>
            )}
          </div>
        </div>

        {review.length > 0 && (
          <div className="mt-8 bg-white rounded-3xl p-6 md:p-8 border border-black/5 shadow-sm space-y-5" data-testid="quiz-review">
            <div className="font-headings font-black text-2xl text-[#1a2332]">Révision des réponses</div>
            {review.map((r, i) => (
              <div key={i} className="border border-black/5 rounded-2xl p-4 bg-[#f8faff]" data-testid={`review-q-${i}`}>
                <div className="flex items-start gap-2">
                  <div className={`shrink-0 w-7 h-7 rounded-full flex items-center justify-center text-xs font-bold ${r.is_correct ? "bg-[#00a86b] text-white" : "bg-[#e21b3c] text-white"}`}>
                    {r.is_correct ? "✓" : "✗"}
                  </div>
                  <div className="font-headings font-bold text-[#1a2332]">{i+1}. {r.type === "fill" ? "Phrase à compléter" : r.q}</div>
                </div>

                {r.type === "fill" ? (
                  <div className="mt-3 space-y-2">
                    <div className="text-sm text-[#1a2332] leading-relaxed">
                      {(() => {
                        const parts = (r.text || "").split("__");
                        return parts.map((p, k) => (
                          <span key={k}>
                            {p}
                            {k < parts.length - 1 && (() => {
                              const correctW = r.blanks?.[k] || "";
                              const userW = r.user_answer?.[k] || "";
                              const ok = userW.toLowerCase().trim() === correctW.toLowerCase().trim();
                              return ok ? (
                                <span className="px-2 py-0.5 mx-0.5 rounded bg-[#00a86b]/10 text-[#007a4e] font-bold">{correctW}</span>
                              ) : (
                                <span className="px-2 py-0.5 mx-0.5 rounded bg-[#e21b3c]/10 text-[#c41632] font-bold">
                                  <span className="line-through opacity-60">{userW || "(vide)"}</span> → {correctW}
                                </span>
                              );
                            })()}
                          </span>
                        ));
                      })()}
                    </div>
                  </div>
                ) : (
                  <div className="mt-3 space-y-2">
                    {(r.choices || []).map((c, j) => {
                      const isCorrect = j === r.correct;
                      const isUser = j === r.user_answer;
                      const bg = isCorrect ? "bg-[#00a86b]/10 border-[#00a86b]/40" : isUser ? "bg-[#e21b3c]/10 border-[#e21b3c]/40" : "bg-white border-black/10";
                      const label = isCorrect && isUser ? "Votre réponse (correcte)" : isCorrect ? "Bonne réponse" : isUser ? "Votre réponse" : null;
                      return (
                        <div key={j} className={`rounded-xl border p-3 ${bg}`} data-testid={`review-q-${i}-choice-${j}`}>
                          <div className="flex items-start gap-2">
                            <span className="text-xs font-bold text-[#1a2332] w-5 mt-0.5">{String.fromCharCode(65+j)}.</span>
                            <div className="flex-1 text-sm text-[#1a2332]">{c}</div>
                            {label && <span className={`text-[10px] uppercase font-bold tracking-wide px-2 py-0.5 rounded-full ${isCorrect ? "bg-[#00a86b] text-white" : "bg-[#e21b3c] text-white"}`}>{label}</span>}
                          </div>
                          {/* Show explanation only for the correct choice */}
                          {isCorrect && r.explanations?.[j] && (
                            <div className="mt-2 pl-7 text-xs italic text-[#1a2332] border-l-2 border-[#00a86b]/40 pl-3 ml-5"
                              data-testid={`review-q-${i}-explanation`}>
                              {r.explanations[j]}
                            </div>
                          )}
                        </div>
                      );
                    })}
                    {r.user_answer === -1 && <div className="text-xs italic text-[#e21b3c]">Pas de réponse donnée (temps écoulé).</div>}
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </div>
    );
  }

  // Active question
  const isFill = q.type === "fill";
  const numBlanks = q.num_blanks || 0;
  const filledCount = Array.from({ length: numBlanks }, (_, k) => k).filter(k => fillSlots[k]).length;
  const allFilled = isFill && numBlanks > 0 && filledCount === numBlanks;

  // Per-card availability in the word bank — supports duplicate words.
  // A bank card is "used" only if more copies of that word are placed than what remains
  // available for cards before/at this index.
  const bankUsed = (() => {
    if (!isFill) return [];
    const bank = q.bank || [];
    const bankCount = {};
    bank.forEach(w => { bankCount[w] = (bankCount[w] || 0) + 1; });
    const placedCount = {};
    Object.values(fillSlots).filter(Boolean).forEach(w => {
      placedCount[w] = (placedCount[w] || 0) + 1;
    });
    const seen = {};
    return bank.map(w => {
      seen[w] = (seen[w] || 0) + 1;
      const available = (bankCount[w] || 0) - (placedCount[w] || 0);
      return seen[w] > available; // true = card grayed out
    });
  })();

  return (
    <div className="max-w-5xl mx-auto" data-testid="quiz-page">
      <div className="flex items-center justify-between text-sm text-[#5b6577] mb-4">
        <div>Question {idx + 1}/{quiz.questions.length}</div>
        <div className="font-headings font-bold text-[#1a2332]">
          {labelOf(level)}
          {isFill
            ? <span className="ml-1 text-[#1368ce]">· sans limite de temps</span>
            : <> · {timeLeft.toFixed(1)}s</>}
        </div>
        <div>Points : <span className="text-[#FF8C42] font-bold">{points}</span></div>
      </div>
      {!isFill && (
        <div className="h-2 bg-[#e5eaf3] rounded-full overflow-hidden">
          <div className="h-full transition-[width] duration-100 ease-linear"
            style={{ width: `${progress}%`, backgroundColor: progress < 30 ? "#e21b3c" : "#00a86b" }} />
        </div>
      )}

      {isFill ? (
        <div className={`mt-6 transition-opacity duration-300 pb-44 lg:pb-0 ${phase === "answer" ? "opacity-100" : "opacity-50 pointer-events-none"}`}>
          <div className="grid grid-cols-1 lg:grid-cols-[1fr_280px] gap-5">
            {/* Sentence on the left */}
            <div className="bg-white border border-black/5 shadow-sm rounded-2xl p-6 md:p-8" data-testid="quiz-fill-question">
              <div className="text-[11px] uppercase tracking-wide text-[#1368ce] font-headings font-bold mb-3">Phrase à compléter</div>
              <div className="text-base md:text-lg text-[#1a2332] leading-loose">
                {(() => {
                  const parts = (q.text || "").split("__");
                  return parts.map((p, k) => (
                    <span key={k}>
                      {p}
                      {k < parts.length - 1 && (
                        <FillSlot index={k} word={fillSlots[k]} status={blankStatus[k]}
                          wrongCount={wrongCount[k] || 0}
                          onClear={() => setBlank(k, undefined)}
                          onReveal={() => revealBlank(k)}
                          onDrop={(word) => setBlank(k, word)} />
                      )}
                    </span>
                  ));
                })()}
              </div>
              <div className="mt-4 text-[11px] text-[#5b6577] flex items-center gap-3 flex-wrap">
                <span>{filledCount}/{q.num_blanks || 0} trou(s) rempli(s)</span>
                {(() => {
                  const okCount = Object.values(blankStatus).filter(s => s === "correct").length;
                  const koCount = Object.values(blankStatus).filter(s => s === "wrong").length;
                  return (
                    <>
                      {okCount > 0 && <span className="text-[#00a86b] font-bold">✓ {okCount} correct(s)</span>}
                      {koCount > 0 && <span className="text-[#e21b3c] font-bold">✗ {koCount} à corriger</span>}
                    </>
                  );
                })()}
                {allFilled && <span className="text-[#00a86b] font-bold">✓ validation automatique…</span>}
              </div>
            </div>

            {/* Word bank — fixed bottom-bar on mobile, sticky aside on desktop */}
            <aside className="fixed bottom-0 left-0 right-0 z-30 bg-[#f8faff]/95 backdrop-blur border-t border-black/10 shadow-[0_-4px_20px_rgba(0,0,0,0.08)] px-3 py-3 lg:static lg:z-auto lg:bg-[#f8faff] lg:border lg:border-black/5 lg:rounded-2xl lg:p-4 lg:shadow-none lg:backdrop-blur-none lg:sticky lg:top-4 lg:self-start lg:max-h-[calc(100vh-2rem)] lg:overflow-y-auto"
              data-testid="quiz-fill-bank">
              <div className="flex items-baseline justify-between gap-2 mb-1 lg:mb-2">
                <div className="text-[11px] uppercase tracking-wide text-[#1368ce] font-headings font-bold">Mots disponibles</div>
                <div className="text-[10px] text-[#5b6577] lg:hidden">{filledCount}/{q.num_blanks || 0}</div>
              </div>
              <div className="hidden lg:block text-[11px] text-[#5b6577] mb-3">Glissez ou cliquez pour placer dans le 1er trou vide.</div>
              <div className="flex gap-2 overflow-x-auto pb-1 lg:flex-wrap lg:overflow-x-visible lg:pb-0 lg:max-h-full">
                {(q.bank || []).map((w, i) => {
                  const used = bankUsed[i];
                  return (
                    <button key={`${w}-${i}`} type="button" disabled={used}
                      draggable={!used}
                      onDragStart={(e) => e.dataTransfer.setData("text/plain", w)}
                      onClick={() => {
                        const slotKeys = Array.from({ length: q.num_blanks }, (_, k) => k);
                        const firstEmpty = slotKeys.find(k => !fillSlots[k]);
                        if (firstEmpty !== undefined) setBlank(firstEmpty, w);
                      }}
                      className={`whitespace-nowrap shrink-0 px-3 py-2 rounded-lg font-headings font-bold text-sm transition ${used ? "bg-[#e5eaf3] text-[#9aa5bc] cursor-not-allowed line-through" : "bg-white border-2 border-[#1368ce]/30 text-[#1368ce] hover:bg-[#1368ce]/10 cursor-grab active:cursor-grabbing"}`}
                      data-testid={`fill-word-${i}`}>
                      {w}
                    </button>
                  );
                })}
              </div>
              <button onClick={submitFill}
                className="mt-2 lg:mt-4 w-full bg-[#00a86b] hover:bg-[#008855] text-white font-headings font-bold rounded-xl px-4 py-2.5 text-sm"
                data-testid="fill-submit-btn">
                {filledCount === 0 ? "Valider sans réponse →" : filledCount < (q.num_blanks || 0) ? `Valider ${filledCount}/${q.num_blanks} →` : "Valider ma réponse →"}
              </button>
            </aside>
          </div>
        </div>
      ) : (
        <>
          <div className="mt-8 bg-white border border-black/5 shadow-sm rounded-2xl p-8 min-h-[140px] flex items-center justify-center text-center">
            <div className="font-headings font-bold text-xl md:text-2xl text-[#1a2332]">{q.q}</div>
          </div>
          {phase === "read" && (
            <div className="mt-3 text-center text-xs text-[#5b6577] italic">Prenez le temps de lire la question…</div>
          )}
          <div className={`mt-6 grid grid-cols-1 md:grid-cols-2 gap-3 transition-opacity duration-300 ${phase === "answer" ? "opacity-100" : "opacity-100"}`}>
            {(q.choices || []).map((c, i) => {
              const fb = mcqFeedback; // null during read/answer, set during between
              const isCorrect = fb && i === fb.correct;
              const isUserWrong = fb && i === fb.user && !fb.ok;
              const dim = fb && !isCorrect && !isUserWrong;
              const ring = isCorrect ? "ring-4 ring-white ring-offset-2 ring-offset-[#00a86b]" : isUserWrong ? "ring-4 ring-white ring-offset-2 ring-offset-[#e21b3c]" : "";
              const bg = isCorrect ? "#00a86b" : isUserWrong ? "#e21b3c" : COLORS[i % 4];
              return (
                <button key={i} onClick={() => phase === "answer" && onAnswer(i)}
                  disabled={phase !== "answer"}
                  data-testid={`quiz-answer-${i}`}
                  className={`relative rounded-2xl p-5 text-left text-white font-headings font-bold text-lg hover:brightness-110 transition-all shadow-md ${ring} ${dim ? "opacity-40" : ""} ${phase === "answer" ? "cursor-pointer" : "cursor-default"}`}
                  style={{ backgroundColor: bg }}>
                  {isCorrect && <span className="absolute top-2 right-3 text-2xl" data-testid={`quiz-correct-${i}`}>✓</span>}
                  {isUserWrong && <span className="absolute top-2 right-3 text-2xl" data-testid={`quiz-wrong-${i}`}>✗</span>}
                  {c}
                </button>
              );
            })}
          </div>
          {mcqFeedback && (
            <div className={`mt-4 rounded-2xl p-4 text-sm border ${mcqFeedback.ok ? "bg-[#00a86b]/10 border-[#00a86b]/30 text-[#007a4e]" : "bg-[#e21b3c]/10 border-[#e21b3c]/30 text-[#1a2332]"}`}
              data-testid="quiz-mcq-feedback">
              <div className="font-headings font-bold mb-1">
                {mcqFeedback.ok ? "✓ Bonne réponse !" : "✗ Mauvaise réponse"}
              </div>
              {mcqFeedback.explanation && (
                <div className="italic text-[#1a2332]">{mcqFeedback.explanation}</div>
              )}
            </div>
          )}
        </>
      )}
    </div>
  );
}

function FillSlot({ index, word, status, wrongCount, onClear, onDrop, onReveal }) {
  const [over, setOver] = useState(false);
  // status: 'idle' | 'checking' | 'correct' | 'wrong' | 'revealed'
  let stateClass = "border-[#9aa5bc] bg-white text-[#9aa5bc]";
  if (over) stateClass = "border-[#00a86b] bg-[#00a86b]/10";
  else if (status === "correct") stateClass = "border-[#00a86b] bg-[#00a86b]/15 text-[#007a4e]";
  else if (status === "revealed") stateClass = "border-[#FF8C42] bg-[#FF8C42]/15 text-[#c45e1a]";
  else if (status === "wrong") stateClass = "border-[#e21b3c] bg-[#e21b3c]/10 text-[#e21b3c]";
  else if (status === "checking") stateClass = "border-[#FF8C42] bg-[#FF8C42]/10 text-[#FF8C42]";
  else if (word) stateClass = "border-[#1368ce] bg-[#1368ce]/10 text-[#1368ce]";
  const showReveal = !word && (wrongCount || 0) >= 2 && status !== "correct" && status !== "revealed";
  return (
    <span className="inline-flex items-center gap-1 align-middle">
      <span
        onDragOver={(e) => { e.preventDefault(); setOver(true); }}
        onDragLeave={() => setOver(false)}
        onDrop={(e) => { e.preventDefault(); setOver(false); const w = e.dataTransfer.getData("text/plain"); if (w) onDrop(w); }}
        onClick={() => word && status !== "revealed" && onClear()}
        data-testid={`fill-slot-${index}`}
        title={status === "revealed" ? "Réponse révélée (compte comme une erreur au score)" : status === "wrong" ? "Cliquez pour retirer et réessayer" : word ? "Cliquez pour retirer le mot" : ""}
        className={`inline-block min-w-[80px] mx-1 px-3 py-1 rounded-lg border-2 border-dashed text-sm font-headings font-bold cursor-pointer transition ${stateClass}`}>
        {status === "correct" && "✓ "}
        {status === "wrong" && "✗ "}
        {status === "revealed" && "👁 "}
        {word || "____"}
      </span>
      {showReveal && (
        <button type="button" onClick={onReveal} data-testid={`fill-reveal-${index}`}
          title="Afficher la bonne réponse (compte comme une erreur au score final)"
          className="text-[10px] font-headings font-bold text-[#FF8C42] hover:bg-[#FF8C42]/10 rounded px-1.5 py-0.5 border border-[#FF8C42]/30">
          Voir
        </button>
      )}
    </span>
  );
}
